import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Layout from '../../components/Layout';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMst } from '../../model/Root';
import { useTranslation } from 'react-i18next';
import { fetchSubscriptionCodes, SubscriptionCodeItem } from '../../services/subscriptionCodeApi';

type PrefixOption = '' | '1P' | '2P' | '1O' | '5P' | 'A';

const PAGE_SIZE_OPTIONS = [50, 100, 200];

interface UnusedCodesScreenProps {
  standalone?: boolean;
}

const UnusedCodesScreen: React.FC<UnusedCodesScreenProps> = observer(({ standalone = true }) => {
  const { t } = useTranslation();
  const {
    userStore: { user },
    errorStore: { setError, setSuccess },
  } = useMst();

  const [loading, setLoading] = useState(false);
  const [allCodes, setAllCodes] = useState<SubscriptionCodeItem[]>([]);

  // Filters
  const [prefix, setPrefix] = useState<PrefixOption>('');
  const [batchQuery, setBatchQuery] = useState('');
  const [codeQuery, setCodeQuery] = useState('');
  const [months, setMonths] = useState<number | ''>('');
  const [excludeGifts, setExcludeGifts] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Selection and pagination
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);

  const isAdmin = !!user?.admin;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const response = await fetchSubscriptionCodes();
      setAllCodes(response);
    } catch (e) {
      setError(t('subscription_codes.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isUsed = (value: unknown): boolean => {
    return value === true || value === 1 || value === 'true' || value === '1';
  };

  const unusedCodes = useMemo(
    () => allCodes.filter((c) => !isUsed((c as unknown as { used: unknown }).used)),
    [allCodes]
  );

  const filtered = useMemo(() => {
    let list = [...unusedCodes];
    if (excludeGifts) {
      list = list.filter(
        (c) =>
          !String(c.code || '')
            .toUpperCase()
            .startsWith('A')
      );
    }
    if (prefix) {
      const pref = String(prefix).toUpperCase();
      list = list.filter((c) =>
        String(c.code || '')
          .toUpperCase()
          .startsWith(pref)
      );
    }
    if (batchQuery) {
      list = list.filter((c) => (c.productId || '').toLowerCase().includes(batchQuery.toLowerCase()));
    }
    if (codeQuery) {
      const q = codeQuery.trim().toLowerCase();
      list = list.filter((c) => (c.code || '').toLowerCase().includes(q));
    }
    if (months !== '') {
      const m = Number(months);
      list = list.filter((c) => Number((c as unknown as { months: unknown }).months) === m);
    }
    if (startDate) {
      const start = new Date(startDate).getTime();
      list = list.filter((c) => (c.createdAt ? new Date(c.createdAt).getTime() >= start : true));
    }
    if (endDate) {
      const end = new Date(endDate).getTime();
      list = list.filter((c) => (c.createdAt ? new Date(c.createdAt).getTime() <= end : true));
    }
    // Sort by createdAt desc, then productId
    list.sort((a, b) => {
      const at = a.createdAt ? new Date(a.createdAt as unknown as string).getTime() : 0;
      const bt = b.createdAt ? new Date(b.createdAt as unknown as string).getTime() : 0;
      if (bt !== at) return bt - at;
      const ap = a.productId || '';
      const bp = b.productId || '';
      return ap.localeCompare(bp);
    });
    return list;
  }, [unusedCodes, excludeGifts, prefix, batchQuery, months, startDate, endDate]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  // Summary chips from filtered set (prefix only)
  const summary = useMemo(() => {
    const total = filtered.length;
    const byPrefix: Record<string, number> = { '1P': 0, '2P': 0, '1O': 0, '5P': 0, A: 0 };
    filtered.forEach((c) => {
      const p =
        c.code.slice(0, 2) === '1P' ||
        c.code.slice(0, 2) === '2P' ||
        c.code.slice(0, 2) === '1O' ||
        c.code.slice(0, 2) === '5P'
          ? c.code.slice(0, 2)
          : c.code.startsWith('A')
            ? 'A'
            : '';
      if (p && byPrefix[p] !== undefined) byPrefix[p] += 1;
    });
    return { total, byPrefix };
  }, [filtered]);

  const toggleSelect = (code: string) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const selectAllOnPage = (checked: boolean) => {
    const currentCodes = paged.map((c) => c.code);
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      currentCodes.forEach((code) => (checked ? next.add(code) : next.delete(code)));
      return next;
    });
  };

  const copySelected = async () => {
    try {
      const codes = filtered.filter((c) => selectedCodes.has(c.code)).map((c) => c.code);
      await navigator.clipboard.writeText(codes.join('\n'));
      setSuccess(t('subscription_codes.copy_success'));
    } catch (e) {
      setError(t('subscription_codes.copy_failed'));
    }
  };

  const exportTxt = () => {
    const content = filtered.map((c) => c.code).join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unused_codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    const header = 'code,months,used,productId,createdAt\n';
    const rows = filtered
      .map((c) => `${c.code},${c.months},${c.used ? 'true' : 'false'},${c.productId ?? ''},${c.createdAt ?? ''}`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unused_codes.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const content = (
    <>
      {!isAdmin && <Alert severity="error">{t('subscription_codes.admin_only')}</Alert>}
      <Box p={3}>
        {/* Filters */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('subscription_codes.code')}
                  placeholder={t('subscription_codes.code_placeholder') || ''}
                  fullWidth
                  value={codeQuery}
                  onChange={(e) => setCodeQuery(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  label={t('subscription_codes.prefix')}
                  fullWidth
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value as PrefixOption)}
                >
                  <MenuItem value="">{t('subscription_codes.any')}</MenuItem>
                  <MenuItem value="1P">1P</MenuItem>
                  <MenuItem value="2P">2P</MenuItem>
                  <MenuItem value="1O">1O</MenuItem>
                  <MenuItem value="5P">5P</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={t('subscription_codes.batch_label')}
                  placeholder={t('subscription_codes.batch_placeholder') || ''}
                  fullWidth
                  value={batchQuery}
                  onChange={(e) => setBatchQuery(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  label={t('subscription_codes.months')}
                  fullWidth
                  value={months}
                  onChange={(e) => setMonths(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  <MenuItem value="">{t('subscription_codes.any')}</MenuItem>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={6}>6</MenuItem>
                  <MenuItem value={12}>12</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  type="date"
                  label={t('subscription_codes.start_date')}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  type="date"
                  label={t('subscription_codes.end_date')}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={<Checkbox checked={excludeGifts} onChange={(e) => setExcludeGifts(e.target.checked)} />}
                  label={t('subscription_codes.exclude_gifts')}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setPrefix('');
                      setBatchQuery('');
                      setCodeQuery('');
                      setMonths('');
                      setStartDate('');
                      setEndDate('');
                      setExcludeGifts(false);
                      setSelectedCodes(new Set());
                      setPage(0);
                    }}
                  >
                    {t('common.reset')}
                  </Button>
                  <Button variant="contained" onClick={fetchAll} disabled={loading}>
                    {loading ? t('reports.scanning') : t('subscription_codes.refresh')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary chips */}
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip color="primary" label={`${t('subscription_codes.total_unused')}: ${summary.total}`} />
          <Chip label={`1P: ${summary.byPrefix['1P']}`} />
          <Chip label={`2P: ${summary.byPrefix['2P']}`} />
          <Chip label={`1O: ${summary.byPrefix['1O']}`} />
          <Chip label={`5P: ${summary.byPrefix['5P']}`} />
          <Chip label={`A: ${summary.byPrefix['A']}`} />
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button variant="outlined" onClick={copySelected} disabled={selectedCodes.size === 0}>
            {t('subscription_codes.copy_selected')}
          </Button>
          <Button variant="outlined" onClick={exportTxt} disabled={filtered.length === 0}>
            {t('subscription_codes.download_txt')}
          </Button>
          <Button variant="outlined" onClick={exportCsv} disabled={filtered.length === 0}>
            {t('subscription_codes.download_csv')}
          </Button>
        </Stack>

        {/* Table */}
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        paged.some((c) => selectedCodes.has(c.code)) && !paged.every((c) => selectedCodes.has(c.code))
                      }
                      checked={paged.length > 0 && paged.every((c) => selectedCodes.has(c.code))}
                      onChange={(e) => selectAllOnPage(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>{t('subscription_codes.code')}</TableCell>
                  <TableCell>{t('subscription_codes.prefix')}</TableCell>
                  <TableCell>{t('subscription_codes.months')}</TableCell>
                  <TableCell>{t('subscription_codes.batch_label')}</TableCell>
                  <TableCell>{t('subscription_codes.created_at')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((row) => {
                  const prefix = row.code.startsWith('A') ? 'A' : row.code.slice(0, 2);
                  return (
                    <TableRow key={row.code} hover>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedCodes.has(row.code)} onChange={() => toggleSelect(row.code)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.code}</Typography>
                      </TableCell>
                      <TableCell>{prefix}</TableCell>
                      <TableCell>{row.months}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.productId}</Typography>
                      </TableCell>
                      <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}</TableCell>
                    </TableRow>
                  );
                })}
                {paged.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Alert severity="info">{t('reports.noneSelected')}</Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        </Card>
      </Box>
    </>
  );
  if (standalone) {
    return <Layout title={t('subscription_codes.unused_title')}>{content}</Layout>;
  }
  return content;
});

export default UnusedCodesScreen;
